/**
   * @function AlarmsTableRow
   * @memberof module:view/Alarms
   * @param {object} props
   * @param {number} props.index (provided by RichTable component)
   * @param {array} props.columns (provided by RichTable component)
   * @param {object} props.row (provided by RichTable component)
   * @returns {ReactElement}
   */
import { useEffect, useRef, useState } from 'react';
import useDevicesStore from 'store/devices';
import useAlarmTypesStore from 'store/alarm-types';
import { getAlarmSeverity } from 'store/alarms';
import { getDeviceUrl } from 'util/device';
import { Link } from 'react-router-dom';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { createModal } from '@thermon-platforms/thermon-ui-core';
import AlarmDetailModal from './alarm-detail-modal';
import { elementIsVisibleInViewport, getScrollParent } from '@thermon-platforms/thermon-ui-core';
import { throttle } from 'lodash';
import { normalizeAlarmType } from 'util/alarm';


function AlarmsTableRow({
  row,
  index,
  selectedRows,
  toggleRow,
  acknowledge,
  acknowledging
}) {
  const root = useRef(null);
  const previousIndex = useRef(index);
  const scrollTop = useRef(0);
  const [render, setRender] = useState(false);
  const [devicesStore] = useDevicesStore();
  const [alarmTypesStore] = useAlarmTypesStore();
  const note = row.noteDetails?.noteText || '';
  const device = devicesStore.shallowDevices.get(row.deviceId);
  const alarmType = alarmTypesStore.types.find(t => t.name == row.alarmType);
  const reading = alarmType ? row.readings.find(r => r.readingType.toLowerCase() == alarmType.parameter) : undefined;

  // If the row is visible in the view port we want to render the full row
  const checkRender = throttle((event) => {
    if (scrollTop.current == event.target.scrollTop || !root.current) return;
    scrollTop.current = event.target.scrollTop;
    if (elementIsVisibleInViewport(root.current, true)) {
      setRender(true);
    } else {
      setRender(false);
    }
  }, 100, {leading: true});

  // When the component loads initially, let's set the initial
  // render state and add our scroll handlers
  useEffect(() => {
    if (elementIsVisibleInViewport(root.current, true)) {
      setRender(true);
    }

    const scrollParent = getScrollParent(root.current);
    scrollParent?.addEventListener('scroll', checkRender);

    return () => {
      scrollParent?.removeEventListener('scroll', checkRender);
    }
  }, []);

  // NOTE - This is a hacky way to force a render check when the
  // parent table rows are changed (usually filtered)
  useEffect(() => {
    if (previousIndex.current == index) return;
    previousIndex.current = index;
    setRender(elementIsVisibleInViewport(root.current, true));
  }, [row, index]);

  function viewAlarmDetail(alarm, device) {
    const alarmDetailModal = createModal(<AlarmDetailModal showNotes={true} device={device.model} alarm={alarm} />);
    alarmDetailModal.open().catch(() => {/* no-op */})
  }

  return (
    <tr key={`alarm-${row.alarmId}-row-${index}`} className={`${getAlarmSeverity(row)}`} ref={root} height="50">
      {render ?
      <>
      <td>
        <input
          type="checkbox"
          checked={selectedRows.some(r => r.alarmId === row.alarmId)}
          onChange={() => toggleRow(row)}
          onClick={event => event.stopPropagation()}
        />
        <span className={`alarm-type ${normalizeAlarmType(row['alarmType'])}`}></span>
        <span className="link" onClick={() => viewAlarmDetail(row, device)}>
          {row?.displayName || row.alarmType}{row.deviceIndex > 0 ? ` (${row.deviceIndex})` : ''}
        </span>
      </td>
      <td>
        {reading ?
          <>{reading.value}{reading.unit?.replace('deg', '°')}</> // Temporary hack to change degC to °C
        :
          <>&mdash;</>
        }
      </td>
      <td>
        {row.setpoint != null ?
          <>{row.setpoint}{row.setpointUnit?.replace('deg', '°')}</> // Temporary hack to change degC to °C
        :
          <>&mdash;</>
        }
      </td>
      <td>
        {device ? <ul className="breadcrumbs">
          {/*
            NOTE - this is the full device path but for now let's just
            show the affected device
          */}
          {/* {device.path?.map((d, i) => {
            if (d.type == 'Site') return null;
            return (
              <li key={`crumb-${row.alarmId}-${i}`}>
                <span className={`device-icon ${d.type.toLowerCase()}`}></span>
                <Link to={getDeviceUrl(devicesStore.shallowDevices.get(d.id))}>
                  {d.name}
                </Link>
              </li>
            );
          })} */}
          <li>
            <span className={`device-icon ${device.model.type.toLowerCase()}`}></span>
            <Link to={getDeviceUrl(device)}>{device.model.name}</Link>
          </li>
        </ul> : <>&mdash;</>}
      </td>
      <td>
        {new Date(row.createdAt).toLocaleString()}
      </td>
      <td>
        {new Date(row.lastUpdatedAt).toLocaleString()}
      </td>
      <td className="column-note">
        {note ?
          <span
            data-tooltip-id="inline-tooltip"
            data-tooltip-content={note + `– ${row.noteDetails?.createdBy} ${new Date(row.noteDetails?.createdAt).toLocaleString()}`}
          >
            {note}
          </span>
         : <>&mdash;</>}
      </td>
      <td className="actions">
        {
          row.acknowledged ? <span className="status-icon acknowledged">Acknowledged</span> :
          <button
            className={`acknowledge ${acknowledging.includes(row) || row.alarmState == 'Acknowledging' ? 'processing' : ''}`}
            onClick={() => acknowledge(row)}
            disabled={acknowledging.includes(row)}
            data-tooltip-content={t`Acknowledge Alarm`}
            data-tooltip-id="inline-tooltip"
          >
            <Trans>Acknowledge</Trans>
          </button>
        }
      </td>
      </> :
      <>
      <td>
        {/*
          NOTE - this input is here just to maintain layout but doesn't
          actually do anything
        */}
        <input
          type="checkbox"
          checked={false}
          onChange={() => {}}
        />
        {/* / */}
        <span className={`alarm-type ${row['alarmType']}`}></span>
        <span className="link" onClick={() => viewAlarmDetail(row, device)}>
          {alarmType?.displayName || row.alarmType}
        </span>
      </td>
      <td colSpan="999"></td>
      </>}
    </tr>
  )
}

export default AlarmsTableRow;
